import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CompanyTimeline = () => {
  const [activeYear, setActiveYear] = useState(2023);

  const milestones = [
    {
      year: 2015,
      title: "Company Founded",
      description: "Xtreem Fibre Solutions established with a vision to transform telecommunications infrastructure",
      achievements: [
        "Initial team of 3 certified technicians",
        "First 10 successful installations",
        "Local business certification obtained"
      ],
      icon: "Rocket"
    },
    {
      year: 2017,
      title: "Enterprise Focus",
      description: "Expanded services to focus on enterprise and industrial installations",
      achievements: [
        "Team expanded to 12 professionals",
        "First major manufacturing client",
        "OSHA safety certifications achieved"
      ],
      icon: "Building"
    },
    {
      year: 2019,
      title: "Technology Leadership",
      description: "Adopted cutting-edge fiber optic technologies and assessment methodologies",
      achievements: [
        "Proprietary assessment process developed",
        "Advanced testing equipment acquired",
        "100+ successful enterprise projects"
      ],
      icon: "Zap"
    },
    {
      year: 2021,
      title: "Regional Expansion",
      description: "Extended service coverage across multiple states with local teams",
      achievements: [
        "Multi-state service coverage",
        "Regional partnerships established",
        "24/7 emergency support launched"
      ],
      icon: "MapPin"
    },
    {
      year: 2023,
      title: "Industry Recognition",
      description: "Achieved industry-leading status with comprehensive service offerings",
      achievements: [
        "500+ projects completed successfully",
        "Industry awards and recognition",
        "Strategic technology partnerships"
      ],
      icon: "Award"
    },
    {
      year: 2024,
      title: "Future Innovation",
      description: "Pioneering next-generation fiber solutions and smart infrastructure",
      achievements: [
        "AI-powered network optimization",
        "Smart building integration services",
        "Sustainability initiative launch"
      ],
      icon: "Lightbulb"
    }
  ];

  const activeMilestone = milestones?.find(m => m?.year === activeYear);

  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-text-primary mb-4">
            Our Journey of Innovation
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            From a small telecommunications contractor to the region's premier fiber optic transformation partner, our timeline reflects continuous growth and innovation.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Timeline Navigation */}
          <div className="lg:col-span-1">
            <div className="space-y-4">
              {milestones?.map((milestone) => (
                <button
                  key={milestone?.year}
                  onClick={() => setActiveYear(milestone?.year)}
                  className={`w-full text-left p-4 rounded-lg transition-all duration-200 ${
                    activeYear === milestone?.year
                      ? 'bg-primary text-white shadow-brand-moderate'
                      : 'bg-surface hover:bg-muted text-text-primary'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      activeYear === milestone?.year
                        ? 'bg-white/20' :'bg-primary/10'
                    }`}>
                      <Icon 
                        name={milestone?.icon} 
                        size={20} 
                        className={activeYear === milestone?.year ? 'text-white' : 'text-primary'}
                      />
                    </div>
                    <div>
                      <div className="font-semibold">{milestone?.year}</div>
                      <div className={`text-sm ${
                        activeYear === milestone?.year ? 'text-white/80' : 'text-text-secondary'
                      }`}>
                        {milestone?.title}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Timeline Content */}
          <div className="lg:col-span-2">
            {activeMilestone && (
              <div className="bg-surface rounded-xl p-8 space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="p-4 bg-primary/10 rounded-xl">
                    <Icon name={activeMilestone?.icon} size={32} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-text-primary">
                      {activeMilestone?.year}
                    </h3>
                    <h4 className="text-xl font-semibold text-primary">
                      {activeMilestone?.title}
                    </h4>
                  </div>
                </div>
                
                <p className="text-lg text-text-secondary leading-relaxed">
                  {activeMilestone?.description}
                </p>
                
                <div className="space-y-4">
                  <h5 className="text-lg font-semibold text-text-primary">
                    Key Achievements
                  </h5>
                  <div className="space-y-3">
                    {activeMilestone?.achievements?.map((achievement, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Icon name="CheckCircle" size={20} className="text-accent flex-shrink-0" />
                        <span className="text-text-secondary">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="pt-6 border-t border-border">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">
                        {activeMilestone?.year === 2024 ? '600+' : 
                         activeMilestone?.year === 2023 ? '500+' :
                         activeMilestone?.year === 2021 ? '300+' :
                         activeMilestone?.year === 2019 ? '100+' :
                         activeMilestone?.year === 2017 ? '50+' : '10+'}
                      </div>
                      <div className="text-sm text-text-secondary">Projects Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">
                        {activeMilestone?.year === 2024 ? '25+' : 
                         activeMilestone?.year === 2023 ? '20+' :
                         activeMilestone?.year === 2021 ? '15+' :
                         activeMilestone?.year === 2019 ? '12+' :
                         activeMilestone?.year === 2017 ? '8+' : '3+'}
                      </div>
                      <div className="text-sm text-text-secondary">Team Members</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyTimeline;